import { useFormik } from "formik";
import { FrequencyFields } from "./frequency-fields";
import {
  UpdateFrequencyRequestDTO,
  useToast,
  useUpdateFrequencyMutation,
} from "@/hooks";
import { useTranslation } from "react-i18next";
import { frequencyValidationSchema } from "./frequency-validation";
import { Button } from "@/components/ui/button";
import { Frequency } from "@/types";
import { AlertDialogDelete } from "../../dialogs";
import { Trash } from "lucide-react";
import { DisplayError } from "@/utils";

type FormFrequencyProps = {
  handleClose: () => void;
  frequency: Frequency;
};

export function FormUpdateFrequency({
  handleClose,
  frequency,
}: FormFrequencyProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const initialValues = {
    id: frequency.id,
    name: frequency.name,
    days: frequency.days.toString(),
  };

  const { mutateAsync, isLoading } = useUpdateFrequencyMutation();
  const { handleSubmit, getFieldProps, errors, touched, resetForm, setFieldValue, setFieldTouched } =
    useFormik({
      initialValues,
      validationSchema: frequencyValidationSchema,
      onSubmit: async (values) => {
        await handleUpdateFrequencySubmit({ ...values, days: Number(values.days) });
      },
    });

  async function handleUpdateFrequencySubmit(
    request: UpdateFrequencyRequestDTO
  ) {
    try {
      await mutateAsync(request, {
        onSuccess: () => {
          toast({
            title: t("LABEL.success"),
            description: t("MESSAGE.created successfully"),
          });
          resetForm();
          handleClose();
        },
        onError: (error) => {
          toast({
            title: t("LABEL.error"),
            description: DisplayError(error, t("MESSAGE.error_message")),
            variant: "destructive",
          });
        },
      });
    } catch (error) {
      toast({
        title: t("LABEL.error"),
        description: DisplayError(error, t("MESSAGE.error_message")),
        variant: "destructive",
      });
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-10 px-4 pb-8">
      <FrequencyFields
        name={getFieldProps("name").value}
        daysProps={getFieldProps("days")}
        onNameChange={(value) => {
          void setFieldTouched("name", true);
          void setFieldValue("name", value);
        }}
        nameError={touched.name ? errors.name : undefined}
        daysError={touched.days ? errors.days : undefined}
      />
      <div className="space-y-2 w-full">
        <Button isLoading={isLoading} className="w-full">
          {t("LABEL.save")}
        </Button>
        <AlertDialogDelete
          endpoint={`/frequencies/${frequency.id}`}
          queryKey="frequencies"
          callBack={handleClose}
        >
          <Button
            type="button"
            className="w-full gap-2 bg-destructive hover:bg-destructive/80 text-destructive-foreground"
          >
            <Trash size={14} />
            <span>{t("LABEL.delete")}</span>
          </Button>
        </AlertDialogDelete>
      </div>
    </form>
  );
}
